/**
 * Действия с карточкой из любого места интерфейса (доска, панель карточки, история):
 * вызывают API, обновляют доску и показывают тосты. Закрытие со статусом,
 * требующим комментарий, открывает диалог через resolvePrompt.
 */
import { ref } from 'vue';
import type { TicketDto } from '@sync-tracker/shared';
import { api } from '@/api';
import { useBoard } from '@/stores/board';
import { useMeta } from '@/stores/meta';
import { useUi } from '@/stores/ui';

export interface ResolvePrompt {
  ticket: TicketDto;
  resolutionKey: string;
  comment: string;
}

/** Глобальное состояние диалога «закрыть с комментарием» (один на приложение). */
export const resolvePrompt = ref<ResolvePrompt | null>(null);

export function useTicketActions() {
  const board = useBoard();
  const meta = useMeta();
  const ui = useUi();
  const busy = ref(false);

  async function run(fn: () => Promise<{ ticket: TicketDto }>, ok?: string) {
    busy.value = true;
    try {
      const { ticket } = await fn();
      board.upsert(ticket);
      if (ok) ui.success(ok);
      return ticket;
    } catch (e) {
      ui.error(e);
      return null;
    } finally {
      busy.value = false;
    }
  }

  const take = (t: TicketDto) => run(() => api.tickets.take(t.id), 'Карточка ваша');
  const release = (t: TicketDto) => run(() => api.tickets.release(t.id));
  const reopen = (t: TicketDto) => run(() => api.tickets.reopen(t.id), 'Карточка снова открыта');
  const comment = (t: TicketDto, text: string) => run(() => api.tickets.comment(t.id, text), 'Комментарий сохранён');

  function resolve(t: TicketDto, resolutionKey: string, commentText?: string) {
    const r = meta.resolution(resolutionKey);
    if (r?.requiresComment && !commentText) {
      resolvePrompt.value = { ticket: t, resolutionKey, comment: '' };
      return Promise.resolve(null);
    }
    return run(() => api.tickets.resolve(t.id, resolutionKey, commentText), `Закрыто: ${r?.title ?? resolutionKey}`);
  }

  const move = (t: TicketDto, columnKey: string) =>
    run(() => api.tickets.move(t.id, columnKey), `Перенесено в «${meta.column(columnKey)?.title ?? columnKey}»`);

  /** Пометить чат категорией прямо из карточки. Если чат перестанет трекаться — его карточки закроются как «Скип». */
  async function markChat(t: TicketDto, categoryKey: string) {
    const cat = meta.category(categoryKey);
    if (cat && !cat.tracked) {
      const ok = await ui.confirm({
        title: `Чат → «${cat.title}»`,
        text: 'Такие чаты не трекаются: открытые карточки этого чата закроются статусом «Скип», новые появляться не будут. Можно вернуть в разделе «Чаты».',
        confirmLabel: 'Пометить',
      });
      if (!ok) return null;
    }
    busy.value = true;
    try {
      const { chat } = await api.chats.setProfile(t.chatId, { categoryKey, trackingOverride: null });
      ui.success(`Чат помечен: ${cat?.title ?? categoryKey}`, chat.tracked ? undefined : 'Трекинг чата выключен');
      return chat;
    } catch (e) {
      ui.error(e);
      return null;
    } finally {
      busy.value = false;
    }
  }

  return { busy, take, release, reopen, comment, resolve, move, markChat };
}
